// src/components/DashboardMobile.jsx
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Box, Chip, Stack, Typography, Paper, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { useIsPremium } from "../hooks/useIsPremium";
import MobileHeader from "./headers/MobileHearder";
import MetricCard from "./MetricCard";
import { PLAN_LIMITS } from "../constants/planLimits";

export default function DashboardMobile() {
  const theme = useTheme();
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const { isPremium } = useIsPremium();
  const navigate = useNavigate();
  const planKey = isPremium ? "premium" : "free";
  const planLimits = PLAN_LIMITS[planKey];
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const formatShort = useCallback((value) => {
    if (value >= 1_000_000) return `${value / 1_000_000}M`;
    if (value >= 1_000) return `${value / 1_000}k`;
    return `${value}`;
  }, []);
  const basicLimits = PLAN_LIMITS.free;
  const premiumLimits = PLAN_LIMITS.premium;

  const [profile, setProfile] = useState(null);
  const [agents, setAgents] = useState([]);
  const [totalConversations, setTotalConversations] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [remainingCredits, setRemainingCredits] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setProfile({ id: snap.id, ...data });
      if (data.darkMode !== undefined) {
        window.dispatchEvent(
          new CustomEvent("darkmode:changed", { detail: data.darkMode })
        );
      }
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!profile?.tenantId) return;

    const agentsCol = collection(db, "tenants", profile.tenantId, "agents");
    const unsubAgents = onSnapshot(
      query(agentsCol, orderBy("createdAt", "desc")),
      (snap) => setAgents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    (async () => {
      try {
        const tSnap = await getDoc(doc(db, "tenants", profile.tenantId));
        setRemainingCredits(tSnap.exists() ? tSnap.data()?.credits || 0 : 0);
      } catch {
    setRemainingCredits(0);
      }
    })();

    return () => {
      unsubAgents();
    };
  }, [profile?.tenantId]);

  useEffect(() => {
    if (!agents.length) {
      setTotalConversations(0);
      setTotalTokens(0);
      return;
    }
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const counts = await Promise.all(
          agents.map(async (agent) => {
            if (!agent?.id) return 0;
            try {
              const convRef = collection(
                db,
                "messages",
                `agent_${agent.id}`,
                "conversations"
              );
              const snapshot = await getCountFromServer(convRef);
              return snapshot.data().count || 0;
            } catch (err) {
              console.error("Failed to count conversations for", agent.id, err);
              return 0;
            }
          })
        );
        if (!cancelled) {
          setTotalConversations(counts.reduce((sum, c) => sum + c, 0));
        }
      } catch (error) {
        console.error("Failed to load conversations count", error);
        if (!cancelled) setTotalConversations(0);
      }
      try {
        const tokenSums = await Promise.all(
          agents.map(async (agent) => {
            if (!agent?.id || !profile?.tenantId) return 0;
            try {
              const usageRef = collection(
                db,
                "tenants",
                profile.tenantId,
                "agents",
                agent.id,
                "usage_daily"
              );
              const snap = await getDocs(usageRef);
              let total = 0;
              snap.forEach((docSnap) => {
                total += Number(docSnap.data()?.totalTokens || 0) || 0;
              });
              return total;
            } catch (err) {
              console.error("Failed to sum tokens for", agent.id, err);
              return 0;
            }
          })
        );
        if (!cancelled) {
          setTotalTokens(tokenSums.reduce((sum, v) => sum + v, 0));
        }
      } catch (err) {
        console.error("Failed to load total tokens", err);
        if (!cancelled) setTotalTokens(0);
      }
    };
    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [agents, profile?.tenantId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <MobileHeader title={translate("Dashboard")} />

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          pt: "40px",
        }}
      >
        <Box sx={{ flex: "0 0 45%", mx: "auto" }}>
          <MetricCard
            title={translate("Agents") || "Agents"}
            value={agents.length}
            color={theme.palette.mode === "dark" ? "#7aa2f7" : "#3b82f6"}
            suffix={
              isPremium
                ? ` / ${planLimits.maxAgents}`
                : ` / ${planLimits.maxAgents} (${
                    translate("PlanUpgradeCTA") || "Go Premium"
                  })`
            }
            helperLines={[
              {
                text: isPremium
                  ? translate("PlanPremiumAgents") || "Premium: 15 agents"
                  : translate("PlanBasicAgents") || "Basic: 3 agents",
              },
              {
                text:
                  translate("PlanUpgradeCTA") || "Go Premium",
                actionLabel: !isPremium
                  ? translate("PlanUpgradeCTA") || "Go Premium"
                  : undefined,
                onClick: !isPremium ? () => navigate("/app/premium") : undefined,
              },
            ]}
          />
        </Box>

        <Box sx={{ flex: "0 0 45%", mx: "auto" }}>
          <MetricCard
            title={translate("Conversations") || "Conversations"}
            value={totalConversations}
            color={theme.palette.mode === "dark" ? "#f5a97f" : "#f59e0b"}
          />
        </Box>

        <Box sx={{ flex: "0 0 45%", mx: "auto" }}>
          <MetricCard
            title={translate("TotalTokensUsed") || "Tokens used"}
            value={Math.round(totalTokens)}
            color={theme.palette.mode === "dark" ? "#c084fc" : "#8b5cf6"}
            animationDuration={200}
            formatter={(val) => numberFormatter.format(val)}
            suffix={
              isPremium
                ? ` / ${formatShort(planLimits.monthlyTokens)}`
                : ` / ${formatShort(planLimits.monthlyTokens)} (${
                    translate("PlanUpgradeCTA") || "Go Premium"
                  })`
            }
            helperLines={[
              {
                text: isPremium
                  ? translate("PlanPremiumTokens") || "Premium: 2M tokens"
                  : translate("PlanBasicTokens") || "Basic: 200k tokens",
              },
              {
                text:
                  translate("PlanUpgradeCTA") || "Go Premium",
                actionLabel: !isPremium
                  ? translate("PlanUpgradeCTA") || "Go Premium"
                  : undefined,
                onClick: !isPremium ? () => navigate("/app/premium") : undefined,
              },
            ]}
          />
        </Box>

        <Box sx={{ flex: "0 0 45%", mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              minHeight: 180,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              {translate("CurrentPlanLabel") || "Your current plan"}
            </Typography>
            <Chip
              label={
                isPremium
                  ? translate("PlanPremiumLabel") || "Premium"
                  : translate("PlanFreeLabel") || "Basic"
              }
              color={isPremium ? "warning" : "default"}
              sx={{
                mt: 1,
                color: isPremium ? "black" : "inherit",
                fontWeight: 600,
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {isPremium
                ? translate("PlanDetailsPremium") ||
                  "Premium: 15 agents / 2M tokens per month."
                : translate("PlanDetailsBasic") ||
                  "Basic: 3 agents / 200k tokens per month."}
            </Typography>
            {!isPremium && (
              <Button
                variant="text"
                onClick={() => navigate("/app/premium")}
                sx={{ mt: 1, textTransform: "none", px: 0 }}
              >
                {translate("PlanUpgradeCTA") || "Go Premium"}
              </Button>
            )}
          </Paper>
        </Box>

      </Box>
    </Box>
  );
}
